import {
  getResponseSingleItem,
  useAssessmentItemResponseScoring,
  useItemResponse,
  useResponseDeclaration
} from "../../helpers"
import { CurrentTestState, setItemResponse } from "../../storage"
import { FC, useEffect, useState } from "react"
import { useSnapshot } from "valtio"
import { useUndoRedoHistoryInput } from "./hooks"
import {
  InteractionType,
  onSaveStudentAnswer,
  isQuestionComplete,
  Variant,
  CheckCorrect,
  NeutralSetting
} from "../../types"
import { EquationEditor } from "./EquationEditor"

interface EquationEditorComponentProps {
  itemId: string
  responseidentifier: string
  children?: JSX.Element | JSX.Element[]
  onSaveStudentAnswer?: onSaveStudentAnswer
  isCompletedQuestion?: isQuestionComplete
  checkCorrect?: CheckCorrect
  isNeutralSetting?: NeutralSetting
}

export const EquationEditorComponent: FC<EquationEditorComponentProps> = ({
  itemId,
  children,
  responseidentifier: responseIdentifier,
  onSaveStudentAnswer,
  isCompletedQuestion,
  checkCorrect,
  isNeutralSetting
}) => {
  const { currentItemResponse, currentTestPart } = useSnapshot(CurrentTestState)

  const itemResponse = useItemResponse(
    currentItemResponse?.id,
    responseIdentifier
  )
  const scoring = useAssessmentItemResponseScoring(
    currentItemResponse?.itemId,
    responseIdentifier
  )

  const { questionCompleted, correctAnswers } = isCompletedQuestion?.() ?? {
    questionCompleted: false,
    correctAnswers: []
  }

  const {
    undo,
    redo,
    currentState: latex,
    setCurrentState: setLatex,
    resetState
  } = useUndoRedoHistoryInput(getResponseSingleItem(itemResponse), 15)

  const [id, setId] = useState("")
  const [variant, setVariant] = useState<Variant>(null)
  const [badge, setBadge] = useState<boolean | null>(null)
  const [isCorrectAnswer, setIsCorrectAnswer] = useState<boolean>(false)
  const [value, setValue] = useState("")
  const [displayValue, setDisplayValue] = useState<string | null>(null)
  const responseDeclaration = useResponseDeclaration(id, responseIdentifier)
  const shouldShowCorrectAnswer =
    correctAnswers?.[0] !== undefined ||
    scoring?.correctResponse?.[0] !== undefined

  useEffect(() => {
    if (!currentItemResponse?.id) {
      return
    }

    setId(CurrentTestState.currentItemResponse.id)
  }, [currentItemResponse?.id, currentItemResponse])

  useEffect(() => {
    if (!currentItemResponse?.id || !responseDeclaration) return
    if (currentTestPart?.isFinished) return

    setItemResponse(
      currentItemResponse.id,
      responseIdentifier,
      [latex],
      true,
      InteractionType.equationEditor,
      responseDeclaration.cardinality
    )
  }, [
    latex,
    responseIdentifier,
    currentItemResponse?.id,
    currentTestPart?.isFinished,
    responseDeclaration
  ])

  useEffect(() => {
    if (currentItemResponse?.id) {
      resetState(getResponseSingleItem(itemResponse))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItemResponse?.id])

  useEffect(() => {
    if (shouldShowCorrectAnswer && correctAnswers?.[0]) {
      if (isNeutralSetting) {
        setVariant(Variant.neutral)
        setBadge(null)
        // In neutral setting, don't show correct answer - keep student's answer
        setDisplayValue(null)
      } else {
        setVariant(Variant.correct)
        setBadge(true)
        // If the answer is correct, show student answer not the correct answer
        if (!isCorrectAnswer) {
          setDisplayValue(correctAnswers?.[0])
        }
      }
    } else if (shouldShowCorrectAnswer && scoring.groupMode) {
      if (!isCorrectAnswer && !isNeutralSetting) {
        setDisplayValue(scoring.correctResponse?.[0] ?? "")
      }
    } else {
      setDisplayValue(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowCorrectAnswer, isCorrectAnswer, isNeutralSetting])

  const handleAnswerSubmit = value => {
    onSaveStudentAnswer?.({
      interactionType: InteractionType.equationEditor,
      answerId: value,
      // If maxRetries = 0, need to use a min and max of 1 to allow for 1 try before showing answer
      minChoices: 1,
      maxChoices: 1
    })

    const isAnswerCorrect = !isNeutralSetting
      ? checkCorrect?.(value.trim())
      : true
    setValue(value)

    if (isNeutralSetting) {
      setVariant(Variant.neutral)
      setBadge(null)
      setIsCorrectAnswer(true)
    } else if (isAnswerCorrect && !isNeutralSetting) {
      setVariant(Variant.correct)
      setBadge(true)
      setIsCorrectAnswer(true)
    } else if (!isAnswerCorrect && !isNeutralSetting) {
      setVariant(Variant.invalid)
      setBadge(false)
      setIsCorrectAnswer(false)
    }
  }

  const isDisabled =
    currentTestPart?.isFinished || questionCompleted || isCorrectAnswer

  const shouldShowSubmit =
    !currentTestPart?.isFinished && !questionCompleted && !isCorrectAnswer

  return (
    <EquationEditor
      // Allow for re-render on dynamic key
      key={`${itemId}-${value}`}
      value={displayValue || latex}
      onChange={setLatex}
      onUndo={undo}
      onRedo={redo}
      onSubmit={() => {
        handleAnswerSubmit(latex)
      }}
      showSubmit={shouldShowSubmit}
      disabled={isDisabled}
      variant={variant}
      badge={badge}
      shouldShowCorrectAnswer={shouldShowCorrectAnswer}
    >
      {children}
    </EquationEditor>
  )
}
